import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  styled,
  Divider,
  Paper,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';

// Contexts und Hooks
import { TextSizeContext } from "data/index";
import { PersonalTabPropType } from "entities/interfaces";
import WithPersonalTabData from "ui/containers/settings/PersonalTabContainer";

// Shared Components
import ImageComponent from "ui/components/shared/ImageComponent";
import TextBox from "ui/components/shared/TextBox";
import { SubmitButton } from "ui/components/shared/SubmitButton";
import ImageUploadModal from "ui/components/pages/settings/modals/ImageUploadModal";

// Assets
import PersonImage from "assets/images/person.jpg";
import CameraIcon from "assets/icons/camera.png";
import { POST } from "api/index";

// === Mobile Header Component ===
const MobileHeader = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  padding: '0.75rem 1rem',
  display: 'flex',
  alignItems: 'center',
  marginBottom: '2rem',
  gap: '1rem',
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  position: 'sticky',
  top: 0,
  zIndex: 100,
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  flex: 1,
}));

// === Mobile Container ===
const MobileContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  paddingBottom: '2rem',
}));

// === Content Wrapper ===
const ContentWrapper = styled(Box)(({ theme }) => ({
  padding: '1.5rem 1rem',
  display: "flex",
  flexDirection: "column",
  gap: "1.5rem",
}));

// === Mobile Card ===
const MobileCard = styled(Paper)(({ theme }) => ({
  padding: "1.5rem",
  borderRadius: "12px",
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  display: "flex",
  flexDirection: "column",
  gap: "1.5rem",
  backgroundColor: theme.palette.common.white,
}));

// === Profile Section (Mobile optimiert) ===
const MobileProfileSection = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "1rem",
  textAlign: "center",
}));

const MobileProfileImage = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "5rem",
  height: "5rem",
  borderRadius: "50%",
  overflow: "hidden",
  boxShadow: theme.shadows[2],
  cursor: "pointer",
}));

const CameraOverlay = styled(Box)(() => ({
  position: "absolute",
  bottom: 0,
  height: "35%",
  width: "100%",
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const ProfileInfoText = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  color: theme.palette.text.secondary,
  textAlign: "center",
  maxWidth: "280px",
}));

// === Form Section ===
const FormSection = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "1.25rem",
}));

// === Component ===
const MobilePersonalSettings = ({
  userData,
  updateUserData,
  uploadImage,
}: PersonalTabPropType) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state: textSize } = useContext(TextSizeContext);

  const [formData, setFormData] = useState({
    image: "",
    firstName: "",
    lastName: "",
    email: "",
  });

  const [error, setError] = useState({ firstName: "", email: "" });
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [imageModal, setImageModal] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  // Profile Image laden
useEffect(() => {
  if (userData) {
    setFormData({
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      image: userData.image || PersonImage,
    });
  }
}, [userData]);

  // Form Handler
  const handleFormChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: keyof typeof formData
  ) => {
    setFormData({ ...formData, [key]: e.target.value });
    setError({ ...error, [key]: "" });
    setSaveDisabled(false);
    setGlobalError(null);
  };

  const handleImageCropped = (croppedUrl: string) => {
    setFormData((prev) => ({ ...prev, image: croppedUrl }));
    setSaveDisabled(false);
    localStorage.setItem("currentUserImage", croppedUrl);
    
    // Event für Header-Update
    window.dispatchEvent(new Event("profileImageChanged"));
  };

  const handleSave = async () => {
    const { firstName, email } = formData;
    if (!firstName || !email) {
      setError({
        firstName: firstName ? "" : `${t("First Name")} ${t("required")}`,
        email: email ? "" : `${t("E-Mail")} ${t("required")}`,
      });
      return;
    }

    try {
      const body = new URLSearchParams();
      body.append("email", email);

      const res = await POST("checkEmailExists", body);
      if (res.result?.exists) {
        setGlobalError(t("This email is already taken"));
        setFormData((prev) => ({ ...prev, email: userData.email }));
        setSaveDisabled(true);
        return;
      }

      const update = await updateUserData(formData);
      if (update.status === 200) {
        setSaveDisabled(true);
        setGlobalError(null);
        
        // Optional: Erfolgs-Toast oder Feedback
        console.log("Profile updated successfully");
      }
    } catch (err) {
      console.error("Fehler beim Speichern:", err);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <MobileContainer>
      {/* Mobile Header */}
      <MobileHeader>
        <IconButton onClick={handleBack} size="small">
          <ArrowBackIcon />
        </IconButton>
        <HeaderTitle>{t("Personal Data")}</HeaderTitle>
      </MobileHeader>

      {/* Content */}
      <ContentWrapper>
        <MobileCard>
          {/* Profile Section */}
          <MobileProfileSection>
            <MobileProfileImage onClick={() => setImageModal(true)}>
              <ImageComponent
                src={formData.image || PersonImage}
                alt="Profilbild"
                width="100%"
                height="100%"
                borderRadius="50%"
              />
              <CameraOverlay>
                <ImageComponent
                  src={CameraIcon}
                  alt="upload"
                  width="1.25rem"
                  height="1.25rem"
                />
              </CameraOverlay>
            </MobileProfileImage>

            <Box>
              <Typography fontWeight={600} fontSize="1rem">
                {t("Profile picture")}
              </Typography>
              <ProfileInfoText>
                {t("A Profile picture helps personalize your account")}
              </ProfileInfoText>
            </Box>
          </MobileProfileSection>

          <Divider />

          {/* Form Section */}
          <FormSection>
            <TextBox
              label={t("First Name")}
              value={formData.firstName}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleFormChange(e, "firstName")
              }
              error={error.firstName}
              isRequired
            />
            
            <TextBox
              label={t("Last Name")}
              value={formData.lastName}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleFormChange(e, "lastName")
              }
            />
            
            <TextBox
              label={t("E-Mail")}
              value={formData.email}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleFormChange(e, "email")
              }
              error={error.email}
              isRequired
            />

            {globalError && (
              <Typography 
                color="error" 
                fontWeight={600}
                fontSize="0.875rem"
                textAlign="center"
              >
                {globalError}
              </Typography>
            )}

            <SubmitButton
              color="primary"
              onClick={handleSave}
              disabled={saveDisabled}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                padding: '0.75rem 1.5rem',
              }}
            >
              {t("Save")}
            </SubmitButton>
          </FormSection>
        </MobileCard>
      </ContentWrapper>

      {/* Image Upload Modal */}
      {imageModal && (
        <ImageUploadModal
          closeModal={() => setImageModal(false)}
          imageUrl={formData.image}
          setImageUrl={handleImageCropped}
          uploadImage={uploadImage}
        />
      )}
    </MobileContainer>
  );
};

export default WithPersonalTabData(MobilePersonalSettings);